import { IAgentMemoryFlag } from '@common/interfaces/atlasai'
import type { RawDraftContentState } from 'draft-js'
import { combineReducers } from 'redux'
import { createLoadingDataReducer } from '../../../helpers/redux'
import { IAction } from '../../apiMiddleware'
import type { IAtlasUser } from '../docs/reducer'
import * as actions from './actions'

export interface IAgentMemorySummary {
  id: string
  type: 'memory' | 'skill'
  key: string
  value: unknown
  valueContentState: RawDraftContentState | null
  category: string | null
  description: string | null
  tags: string[]
  scope: 'global' | 'user'
  sourceType: string
  confidence: number
  successCount: number
  failureCount: number
  hitCount: number
  ttlHours: number | null
  createdByUserId: string | null
  createdByUser?: IAtlasUser | null
  updatedByUserId: string | null
  updatedByUser?: IAtlasUser | null
  createdAt: string
  updatedAt: string
  /** Count of open (unresolved) dream-cycle contradiction/duplicate flags. */
  activeFlagCount?: number
}

export interface IAgentMemory extends IAgentMemorySummary {
  sourceRefs: unknown[]
  lastValidatedAt: string | null
  /** Open dream-cycle contradiction/duplicate flags (loaded on Get, for the edit view). */
  flags?: IAgentMemoryFlag[]
}

export interface IAgentMemoryListData {
  data: IAgentMemorySummary[]
  total: number
}

export default combineReducers({
  list: createLoadingDataReducer<IAgentMemoryListData>(
    [actions.LIST_MEMORIES_REQUEST],
    [actions.ADD_MEMORY_REQUEST, actions.UPDATE_MEMORY_REQUEST, actions.DELETE_MEMORY_REQUEST],
    // withSaveActionLiveEdit — removes deleted rows from the list in place
    // and patches updated rows without waiting for a full refetch.
    true,
  ),

  selected(state: IAgentMemory | null = null, action: IAction<IAgentMemory>): IAgentMemory | null {
    switch (action.type) {
      case actions.GET_MEMORY_SUCCESS:
        return action.data
      case actions.UPDATE_MEMORY_SUCCESS:
        if (state && action.data?.id === state.id) return action.data
        return state
      case actions.RESOLVE_MEMORY_FLAGS_SUCCESS:
        // Only clear the flags; keep the rest of `selected` so an in-progress
        // edit in the modal isn't disturbed (the form keeps dirty fields).
        if (state && action.data?.id === state.id) return { ...state, flags: [] }
        return state
      case actions.CLEAR_SELECTED_MEMORY:
      case actions.DELETE_MEMORY_SUCCESS:
        return null
      default:
        return state
    }
  },
})
